import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Calendar,
  Mail,
  Bot,
  Sparkles,
  Youtube,
  LogOut,
  Settings as SettingsIcon,
  Move,
  Eye,
  EyeOff,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  ArrowRight,
  ExternalLink,
  Home,
  Link2,
  UserCircle,
  MessageSquare,
  Loader2,
  Activity,
  Send,
  Radio,
  Check,
  X,
  Smartphone,
  BarChart3,
  Kanban,
  Layers,
  LayoutGrid,
  Rows,
  ArrowUpRight,
  DollarSign,
  Inbox,
  Hourglass,
} from "lucide-react";
import { toast } from "sonner";
import { FloatingOrbs, AmbientDust, PulseDot } from "@/components/landing/AmbientMotion";
import MondayEventsFeed from "@/components/admin/MondayEventsFeed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * /admin — central hub landing after login. Shows a card grid to every admin
 * area + live snapshot counters so you know where the action is at a glance.
 * Nothing here duplicates existing pages — this is purely a router with a
 * pulse. Each card jumps you into the dedicated tool.
 */
const AdminHub = () => {
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [activity, setActivity] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Customization state — persisted per-admin in Mongo dashboard_prefs
  const [editing, setEditing] = useState(false);
  const [cardOrder, setCardOrder] = useState([]);     // list of card.key in preferred order
  const [hiddenCards, setHiddenCards] = useState([]); // list of card.key that are hidden
  const [viewMode, setViewMode] = useState("cards");  // "cards" (full) | "pills" (compact)
  const [pillDetail, setPillDetail] = useState(null); // card being previewed in the pill popup
  const [dragKey, setDragKey] = useState(null);

  // Keyboard shortcuts — Notion-style single-key jumps (only when NO input focused)
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const map = {
        g: "/admin/guests",
        b: "/admin/bookings",
        m: "/admin/marketing",
        p: "/admin/pipeline",
        a: "/admin/analytics",
        c: "/admin/chat-leads",
        s: "/admin/settings",
        h: "/admin",
      };
      const key = e.key.toLowerCase();
      if (map[key]) {
        e.preventDefault();
        navigate(map[key]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate]);

  useEffect(() => {
    loadHubData();
    // Poll revenue every 30s so the tile can pulse on new payments even
    // when the tab has been open for a while.
    const revPoll = setInterval(() => {
      axios
        .get(`${API}/admin/payments/revenue-stats`, { headers: authHeaders() })
        .then(({ data }) => setSnapshot((prev) => (prev ? { ...prev, revenue: data } : prev)))
        .catch(() => { /* silent — user is either logged out or offline */ });
    }, 30_000);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/dashboard/prefs`, { headers: authHeaders() });
        setCardOrder(data.card_order || []);
        setHiddenCards(data.hidden_cards || []);
        setViewMode(data.view_mode === "pills" ? "pills" : "cards");
      } catch (err) {
        // 401 = not logged in yet, or first-run — silent. Everything else is worth surfacing.
        if (err?.response?.status !== 401) {
          console.warn("[AdminHub] dashboard prefs load failed:", err?.message || err);
        }
      }
    })();
    return () => clearInterval(revPoll);
  }, []);

  const persistPrefs = useCallback(async (nextOrder, nextHidden, nextViewMode) => {
    try {
      const patch = {
        card_order: nextOrder,
        hidden_cards: nextHidden,
      };
      if (nextViewMode !== undefined) patch.view_mode = nextViewMode;
      await axios.patch(`${API}/admin/dashboard/prefs`, patch, { headers: authHeaders() });
    } catch (err) {
      console.warn("[AdminHub] persist dashboard prefs failed (will retry on next change):", err?.message || err);
    }
  }, []);

  const toggleViewMode = () => {
    const next = viewMode === "pills" ? "cards" : "pills";
    setViewMode(next);
    persistPrefs(cardOrder, hiddenCards, next);
  };

  const loadHubData = useCallback(async () => {
    try {
      const headers = authHeaders();
      const [me, aud, cnt, anly, bk, act, cl, pend, rev] = await Promise.all([
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/admin/marketing/audience`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/guest-requests/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/sable/analytics?days=30`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/bookings/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/hub/activity?hours=24&limit=12`, { headers }).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/admin/chat-leads?limit=1`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/clients/pending`, { headers }).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/admin/payments/revenue-stats`, { headers }).catch(() => ({ data: null })),
      ]);
      setUser(me.data);
      setSnapshot({
        paid_clients: aud.data.paid_clients || 0,
        guests: aud.data.guests || 0,
        prospects: aud.data.prospects || 0,
        reopen_waitlist: aud.data.reopen_waitlist || 0,
        pending_clients: pend.data.count || 0,
        pending_guest_requests: cnt.data.pending || 0,
        total_chats: anly.data.total_chats || 0,
        bookings_from_chat: anly.data.bookings_from_chat || 0,
        contact_capture_rate: anly.data.contact_capture_rate || 0,
        pending_bookings: bk.data.pending_review || 0,
        booked_bookings: bk.data.booked || 0,
        chat_leads_total: cl.data.total || 0,
        chat_leads_escalated: cl.data.escalated || 0,
        revenue: rev.data || null,
      });
      setActivity(act.data.events || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load hub snapshot.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("sb_admin_token");
    toast.success("Signed out.");
    navigate("/admin/login", { replace: true });
  };

  const cards = [
    {
      key: "inbox",
      to: "/admin/inbox",
      icon: Inbox,
      color: "#22c55e",
      title: "Admin inbox",
      sub: "One place for approvals · team logins · media audits",
      stat:
        (snapshot?.pending_guest_requests || 0) +
        (snapshot?.pending_bookings || 0) +
        (snapshot?.pending_clients || 0),
      statLabel: "pending",
      testId: "admin-card-inbox",
      category: "pipeline",
    },
    {
      key: "guests",
      to: "/admin/guests",
      icon: Users,
      color: "#ff0055",
      title: "Spotlight guests",
      sub: "Approve, decline, or reschedule podcast guest requests",
      stat: snapshot?.pending_guest_requests,
      statLabel: "pending",
      category: "pipeline",
    },
    {
      key: "bookings",
      to: "/admin/bookings",
      icon: Calendar,
      color: "#00f0ff",
      title: "Studio bookings",
      sub: "Paid session reservations · confirm and cancel",
      stat: snapshot?.pending_bookings,
      statLabel: "pending",
      category: "pipeline",
    },
    {
      key: "marketing",
      to: "/admin/marketing",
      icon: Mail,
      color: "#a78bfa",
      title: "Marketing & CRM",
      sub: "Paid clients · campaigns · Kanban boards · labels",
      stat: snapshot?.paid_clients,
      statLabel: "paid clients",
      category: "growth",
    },
    {
      key: "chat_leads",
      to: "/admin/chat-leads",
      icon: MessageSquare,
      color: "#22c55e",
      title: "Chat leads",
      sub: "Recap of every Sable capture · escalations · delete stale ones",
      stat: snapshot?.chat_leads_total,
      statLabel: `${snapshot?.chat_leads_escalated || 0} escalated`,
      testId: "admin-card-chat-leads",
      category: "growth",
    },
    {
      key: "reopen_waitlist",
      to: "/admin/marketing",
      icon: Hourglass,
      color: "#f5c065",
      title: "Reopen waitlist",
      sub: "Visitors who asked to be notified when the new studio opens",
      stat: snapshot?.reopen_waitlist,
      statLabel: snapshot?.reopen_waitlist === 1 ? "person waiting" : "people waiting",
      testId: "admin-card-reopen-waitlist",
      category: "growth",
    },
    {
      key: "payments",
      to: "/admin/payments",
      icon: DollarSign,
      color: "#22c55e",
      title: "Payments & retainers",
      sub: "Ledger · refund from the row · cancel or upgrade retainers",
      stat: (snapshot?.revenue?.month?.count ?? 0),
      statLabel: "paid · month",
      testId: "admin-card-payments",
      category: "revenue",
    },
    {
      key: "sable",
      to: "/admin/marketing#sable",
      icon: Bot,
      color: "#22c55e",
      title: "Sable AI analytics",
      sub: "Chat → booking conversion + copy-hint feedback loop",
      stat: snapshot?.total_chats,
      statLabel: "chats · 30d",
      category: "growth",
    },
    {
      key: "compose",
      to: "/admin/marketing#compose",
      icon: Sparkles,
      color: "#ff0055",
      title: "Compose campaign",
      sub: "Auto-draft from top performer · schedule for later",
      stat: snapshot?.guests + snapshot?.paid_clients + snapshot?.prospects || 0,
      statLabel: "total reach",
      category: "growth",
    },
    {
      key: "pipeline",
      to: "/admin/pipeline",
      icon: Kanban,
      color: "#00f0ff",
      title: "Studio pipeline",
      sub: "Drag bookings + guests + leads across columns · Monday-free",
      stat: (snapshot?.pending_bookings ?? 0) + (snapshot?.pending_guest_requests ?? 0),
      statLabel: "in flight",
      testId: "admin-card-pipeline",
      category: "pipeline",
    },
    {
      key: "analytics",
      to: "/admin/analytics",
      icon: BarChart3,
      color: "#22c55e",
      title: "Analytics",
      sub: "Revenue, funnel, response-time · pulled from Mongo live",
      stat: null,
      statLabel: "live · 90d",
      testId: "admin-card-analytics",
      category: "revenue",
    },
    {
      key: "install",
      to: "/admin/install",
      icon: Smartphone,
      color: "#00f0ff",
      title: "Install as app",
      sub: "Scan a QR — put Command Center on your home screen",
      stat: null,
      statLabel: "1-tap · standalone",
      testId: "admin-card-install",
      category: "setup",
    },
  ];

  // Mobile-only category grouping — desktop still uses the flat grid/pills.
  // Order here = accordion order. Each group shows a count of pending items
  // rolled up from its children so admins know where to look first.
  const MOBILE_CATEGORIES = [
    {
      key: "pipeline",
      label: "Pipeline",
      sub: "Bookings, guests, and the flow board",
      icon: Kanban,
      color: "#00f0ff",
    },
    {
      key: "growth",
      label: "Growth",
      sub: "Marketing, campaigns, chat leads, and Sable AI",
      icon: Sparkles,
      color: "#ff0055",
    },
    {
      key: "revenue",
      label: "Revenue",
      sub: "Payments, retainers, and analytics",
      icon: DollarSign,
      color: "#22c55e",
    },
    {
      key: "setup",
      label: "Setup",
      sub: "Install as an app on this phone",
      icon: Smartphone,
      color: "#a78bfa",
    },
  ];

  // Apply user's persisted order + hide filter
  const orderedCards = (() => {
    const byKey = new Map(cards.map((c) => [c.key, c]));
    const inOrder = cardOrder.map((k) => byKey.get(k)).filter(Boolean);
    const remaining = cards.filter((c) => !cardOrder.includes(c.key));
    return [...inOrder, ...remaining];
  })();
  const visibleCards = editing ? orderedCards : orderedCards.filter((c) => !hiddenCards.includes(c.key));

  const reorderCard = (key, targetIdx) => {
    const withoutKey = orderedCards.filter((c) => c.key !== key).map((c) => c.key);
    withoutKey.splice(targetIdx, 0, key);
    setCardOrder(withoutKey);
    persistPrefs(withoutKey, hiddenCards);
  };
  const shiftCard = (key, delta) => {
    const currentKeys = orderedCards.map((c) => c.key);
    const idx = currentKeys.indexOf(key);
    if (idx === -1) return;
    const nextIdx = Math.max(0, Math.min(currentKeys.length - 1, idx + delta));
    if (nextIdx === idx) return;
    const next = currentKeys.slice();
    next.splice(idx, 1);
    next.splice(nextIdx, 0, key);
    setCardOrder(next);
    persistPrefs(next, hiddenCards);
  };
  const toggleHidden = (key) => {
    const next = hiddenCards.includes(key) ? hiddenCards.filter((k) => k !== key) : [...hiddenCards, key];
    setHiddenCards(next);
    persistPrefs(cardOrder, next);
  };
  const resetLayout = async () => {
    if (!window.confirm("Reset your dashboard layout back to default?")) return;
    try {
      await axios.delete(`${API}/admin/dashboard/prefs`, { headers: authHeaders() });
      setCardOrder([]);
      setHiddenCards([]);
      toast.success("Layout reset");
    } catch { toast.error("Reset failed"); }
  };

  const publicLinks = [
    { icon: Home, label: "Main site", href: "/" },
    { icon: Link2, label: "Public /links page", href: "/links" },
    { icon: UserCircle, label: "Client Portal", href: "/client-portal" },
  ];

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden" data-testid="admin-hub-page">
      <FloatingOrbs colors={["#00f0ff", "#ff0055", "#a78bfa"]} />
      <AmbientDust count={18} color="#00f0ff" />
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[8%] left-[50%] -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#6b21d9]/22 blur-[160px]" />
        <div className="absolute bottom-[-15%] right-[10%] w-[420px] h-[420px] rounded-full bg-[#00f0ff]/12 blur-[140px]" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#22c55e] mb-3">
              <PulseDot color="#22c55e" size={7} />
              SB Media Admin
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              Command <span className="text-[#00f0ff] text-glow-cyan">center.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              {user?.email && <>Signed in as <span className="text-white">{user.email}</span> · </>}
              pick a lane and go.
            </p>
          </div>
          <div className="flex items-center flex-wrap gap-2 self-start md:self-auto">
            <MondayEventsFeed token={getToken()} />
            <div
              className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-[10px] uppercase tracking-[0.28em] text-[#a395c3]"
              title="Keyboard shortcuts: press G / B / M / H to jump between sections"
              data-testid="admin-hub-kbd-hint"
            >
              <Radio size={11} className="text-[#22c55e]" />
              <kbd className="font-mono text-white/70">g</kbd>·
              <kbd className="font-mono text-white/70">b</kbd>·
              <kbd className="font-mono text-white/70">m</kbd>
            </div>
            <Link
              to="/admin/settings"
              data-testid="admin-hub-settings"
              aria-label="Settings"
              title="Settings"
              className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 text-white/70 hover:bg-white/[0.08] hover:text-[#00f0ff] hover:border-[#00f0ff]/40 flex items-center justify-center transition-colors"
            >
              <SettingsIcon size={15} strokeWidth={2} />
            </Link>
            {!editing && (
              <button
                onClick={toggleViewMode}
                data-testid="admin-hub-view-mode"
                title={viewMode === "pills" ? "Switch to full cards" : "Switch to compact pills"}
                aria-label="Toggle view mode"
                className="w-9 h-9 md:w-auto md:h-auto md:px-3 md:py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-white/80 hover:bg-white/[0.08] hover:text-[#00f0ff] hover:border-[#00f0ff]/40 transition inline-flex items-center gap-2 justify-center"
              >
                {viewMode === "pills"
                  ? <LayoutGrid size={13} />
                  : <Rows size={13} />}
                <span className="hidden md:inline">{viewMode === "pills" ? "Cards" : "Pills"}</span>
              </button>
            )}
            {editing ? (
              <>
                <button
                  onClick={resetLayout}
                  data-testid="admin-hub-reset-layout"
                  title="Reset to default"
                  aria-label="Reset layout"
                  className="w-9 h-9 md:w-auto md:h-auto md:px-3 md:py-2 rounded-full bg-white/[0.05] border border-white/15 hover:bg-white/[0.1] text-xs inline-flex items-center gap-2 justify-center"
                >
                  <RotateCcw size={12} /> <span className="hidden md:inline">Reset</span>
                </button>
                <button
                  onClick={() => setEditing(false)}
                  data-testid="admin-hub-done-editing"
                  aria-label="Done"
                  className="w-9 h-9 md:w-auto md:h-auto md:px-4 md:py-2 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white inline-flex items-center justify-center gap-2"
                >
                  <Check size={14} className="md:hidden" />
                  <span className="hidden md:inline">Done</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setEditing(true)}
                data-testid="admin-hub-customize"
                title="Customize dashboard"
                aria-label="Customize dashboard"
                className="w-9 h-9 md:w-auto md:h-auto md:px-3 md:py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-white/80 hover:bg-white/[0.08] hover:text-[#00f0ff] hover:border-[#00f0ff]/40 transition inline-flex items-center gap-2 justify-center"
              >
                <Move size={13} /> <span className="hidden md:inline">Customize</span>
              </button>
            )}
            <button
              onClick={logout}
              data-testid="admin-hub-logout"
              title="Sign out"
              aria-label="Sign out"
              className="w-9 h-9 md:w-auto md:h-auto md:px-4 md:py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm text-white/80 hover:bg-white/[0.08] hover:text-white transition inline-flex items-center gap-2 justify-center"
            >
              <LogOut size={14} /> <span className="hidden md:inline">Sign out</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin text-[#00f0ff]" />
          </div>
        ) : (
          <>
            {/* Main cards grid */}
            {editing && (
              <div className="mb-4 rounded-2xl border border-[#00f0ff]/30 bg-[#00f0ff]/[0.06] p-4 text-sm text-white/85" data-testid="admin-hub-edit-banner">
                <b className="text-[#00f0ff]">Customize mode.</b> Tap the ↑/↓ arrows to reorder, or drag on desktop. Tap the eye to hide/show. Saves automatically.
              </div>
            )}
            {viewMode === "pills" && !editing ? (
              <div className="hidden md:flex flex-wrap gap-2" data-testid="admin-hub-pills">
                {visibleCards.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <motion.button
                      key={c.key}
                      onClick={() => setPillDetail(c)}
                      data-testid={`admin-hub-pill-${c.key}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: Math.min(i * 0.025, 0.3) }}
                      className="group inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border transition-colors"
                      style={{
                        background: `${c.color}0d`,
                        borderColor: `${c.color}40`,
                      }}
                    >
                      <span
                        className="w-6 h-6 rounded-full flex items-center justify-center border flex-shrink-0"
                        style={{ background: `${c.color}25`, borderColor: `${c.color}66`, color: c.color }}
                      >
                        <Icon size={11} />
                      </span>
                      <span className="text-[12px] font-semibold whitespace-nowrap">{c.title}</span>
                      {c.stat !== null && c.stat !== undefined && (
                        <span
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{ background: `${c.color}22`, color: c.color }}
                        >
                          {c.stat}
                        </span>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            ) : null}

            {/* Mobile-only categorized accordion — collapses the 10 top-level
                tiles into 4 tap-to-expand groups so the small-screen landing
                page isn't a wall of cards. Desktop keeps the pill/grid view
                above. When editing (reorder mode) we skip this so the drag
                handles stay reachable. */}
            {!editing && (
              <MobileCategoryNav
                categories={MOBILE_CATEGORIES}
                cards={visibleCards}
              />
            )}

            {/* Desktop grid — hidden on mobile when not editing so we don't
                render the giant vertical stack twice. */}
            {!(viewMode === "pills" && !editing) && (
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${editing ? "" : "hidden md:grid"}`}>
              {visibleCards.map((c, i) => {
                const Icon = c.icon;
                const isHidden = hiddenCards.includes(c.key);
                const isDragging = dragKey === c.key;
                const CardWrapper = editing ? "div" : Link;
                const wrapperProps = editing
                  ? {}
                  : { to: c.to };
                return (
                  <motion.div
                    key={c.key}
                    draggable={editing}
                    onDragStart={(e) => {
                      if (!editing) return;
                      setDragKey(c.key);
                      try { e.dataTransfer.setData("text/plain", c.key); } catch (_err) {
                        // Safari sometimes rejects setData on synthetic events — drag still works via state.
                      }
                    }}
                    onDragEnd={() => setDragKey(null)}
                    onDragOver={(e) => editing && e.preventDefault()}
                    onDrop={(e) => {
                      if (!editing) return;
                      e.preventDefault();
                      if (dragKey && dragKey !== c.key) reorderCard(dragKey, i);
                      setDragKey(null);
                    }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: isHidden ? 0.4 : 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                    className={editing ? (isDragging ? "opacity-40 scale-95" : "cursor-grab active:cursor-grabbing") : ""}
                  >
                    <CardWrapper
                      {...wrapperProps}
                      data-testid={`admin-hub-card-${c.key}`}
                      className={`group relative block rounded-3xl border backdrop-blur-md p-6 md:p-7 transition-all overflow-hidden ${
                        isHidden
                          ? "border-dashed border-white/15 bg-white/[0.015]"
                          : "border-white/10 bg-white/[0.03] hover:-translate-y-1 hover:border-white/25 hover:bg-white/[0.055]"
                      }`}
                    >
                      {/* Corner glow on hover */}
                      <div
                        className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                        style={{ background: c.color }}
                      />
                      {editing && (
                        <div className="absolute top-3 right-3 flex items-center gap-1.5 z-10">
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); shiftCard(c.key, -1); }}
                            disabled={i === 0}
                            data-testid={`admin-hub-move-up-${c.key}`}
                            title="Move up"
                            className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/15 text-white/70 hover:bg-[#00f0ff]/15 hover:text-[#00f0ff] hover:border-[#00f0ff]/40 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); shiftCard(c.key, 1); }}
                            disabled={i === visibleCards.length - 1}
                            data-testid={`admin-hub-move-down-${c.key}`}
                            title="Move down"
                            className="w-8 h-8 rounded-full bg-white/[0.05] border border-white/15 text-white/70 hover:bg-[#00f0ff]/15 hover:text-[#00f0ff] hover:border-[#00f0ff]/40 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleHidden(c.key); }}
                            data-testid={`admin-hub-toggle-hide-${c.key}`}
                            title={isHidden ? "Show" : "Hide"}
                            className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
                              isHidden
                                ? "bg-white/[0.04] border-white/15 text-white/60 hover:bg-[#00f0ff]/15 hover:text-[#00f0ff]"
                                : "bg-white/[0.05] border-white/15 text-white/70 hover:bg-[#ff0055]/15 hover:text-[#ff0055] hover:border-[#ff0055]/40"
                            }`}
                          >
                            {isHidden ? <Eye size={12} /> : <EyeOff size={12} />}
                          </button>
                        </div>
                      )}
                      <div className="relative flex items-start justify-between mb-4">
                        <div
                          className="w-12 h-12 rounded-2xl flex items-center justify-center border"
                          style={{
                            background: `${c.color}18`,
                            borderColor: `${c.color}45`,
                            color: c.color,
                          }}
                        >
                          <Icon size={20} />
                        </div>
                        {typeof c.stat === "number" && !editing && (
                          <div className="text-right">
                            <div className="text-3xl font-display font-black" style={{ color: c.color }}>
                              {c.stat}
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.22em] text-[#a395c3]">
                              {c.statLabel}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <div className="font-display font-bold text-lg tracking-tight mb-1.5">
                          {c.title}
                        </div>
                        <p className="text-[13px] text-[#a395c3] leading-relaxed">{c.sub}</p>
                        {!editing && (
                          <div className="flex items-center gap-1.5 mt-4 text-[11px] uppercase tracking-[0.24em] font-semibold" style={{ color: c.color }}>
                            Open <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                          </div>
                        )}
                        {editing && isHidden && (
                          <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-white/40">
                            Hidden — tap the eye to show
                          </div>
                        )}
                      </div>
                    </CardWrapper>
                  </motion.div>
                );
              })}
            </div>
            )}

            {/* Snapshot strip */}
            {snapshot && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-6 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-6 md:p-7"
                data-testid="admin-hub-snapshot"
              >
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#00f0ff]/15 border border-[#00f0ff]/40 text-[#00f0ff] flex items-center justify-center">
                    <MessageSquare size={16} />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                      Live snapshot
                    </div>
                    <div className="font-display font-bold text-lg tracking-tight">
                      What&apos;s happening right now
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <SnapStat
                    label="Paid clients"
                    value={snapshot.paid_clients}
                    color="#00f0ff"
                    to="/admin/marketing?segment=paid_clients"
                    testid="snap-stat-paid-clients"
                  />
                  <SnapStat
                    label="Spotlight guests"
                    value={snapshot.guests}
                    color="#ff0055"
                    to="/admin/guests"
                    testid="snap-stat-guests"
                  />
                  <SnapStat
                    label="Prospects"
                    value={snapshot.prospects}
                    color="#a78bfa"
                    to="/admin/boards/clients?column=pending_approval"
                    testid="snap-stat-prospects"
                    badge={snapshot.pending_clients || 0}
                  />
                  <SnapStat
                    label="Total chats · 30d"
                    value={snapshot.total_chats}
                    color="#22c55e"
                    to="/admin/chat-leads"
                    testid="snap-stat-chats"
                  />
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-[#a395c3]">
                  <span>Contact-capture rate: <span className="text-white font-semibold">{Math.round(snapshot.contact_capture_rate * 100)}%</span></span>
                  <span>·</span>
                  <span>Bookings from chat: <span className="text-white font-semibold">{snapshot.bookings_from_chat}</span></span>
                </div>
              </motion.div>
            )}

            {/* Revenue tile — Stripe payments today / week / month */}
            {snapshot && snapshot.revenue && (
              <RevenueTile revenue={snapshot.revenue} />
            )}

            {/* Live activity feed */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="mt-6 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md p-6 md:p-7"
              data-testid="admin-hub-activity"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center">
                  <Activity size={16} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#22c55e]">
                    Live · last 24h
                  </div>
                  <div className="font-display font-bold text-lg tracking-tight">
                    Recent activity
                  </div>
                </div>
              </div>
              {activity.length === 0 ? (
                <div className="text-sm text-[#a395c3] italic py-4">Nothing new in the last 24 hours.</div>
              ) : (
                <ul className="space-y-2">
                  {activity.map((e, i) => (
                    <ActivityRow
                      key={(e.id || "") + i}
                      event={e}
                      index={i}
                      onActed={loadHubData}
                    />
                  ))}
                </ul>
              )}
            </motion.div>

            {/* Public URLs to jump out to */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.65 }}
              className="mt-6 flex flex-wrap gap-2"
            >
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center mr-2">
                Public URLs
              </span>
              {publicLinks.map((p) => {
                const Icon = p.icon;
                return (
                  <a
                    key={p.href}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-hub-public-${p.href.replace(/\//g, "")}`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-xs text-white/80 hover:bg-white/[0.08] hover:text-white transition"
                  >
                    <Icon size={11} /> {p.label}
                    <ExternalLink size={9} className="opacity-60" />
                  </a>
                );
              })}
            </motion.div>
          </>
        )}
      </div>

      {/* Pill detail popup — opens when user taps a compact pill */}
      <AnimatePresence>
        {pillDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center p-3 md:p-6"
            onClick={() => setPillDetail(null)}
            data-testid="admin-hub-pill-detail"
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-3xl border p-6 relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${pillDetail.color}18 0%, #0b0715 55%)`,
                borderColor: `${pillDetail.color}55`,
              }}
            >
              <button
                onClick={() => setPillDetail(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.06] border border-white/15 hover:bg-white/[0.15] flex items-center justify-center"
                data-testid="admin-hub-pill-detail-close"
              >
                <X size={13} />
              </button>
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center border-2"
                  style={{
                    background: `${pillDetail.color}25`,
                    borderColor: `${pillDetail.color}66`,
                    color: pillDetail.color,
                  }}
                >
                  <pillDetail.icon size={22} />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: pillDetail.color }}>
                    Command center
                  </div>
                  <h2 className="font-display font-bold text-2xl tracking-tight truncate">{pillDetail.title}</h2>
                </div>
              </div>
              <p className="text-sm text-white/70 mb-5 leading-relaxed">{pillDetail.sub}</p>
              {pillDetail.stat !== null && pillDetail.stat !== undefined && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-5">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mb-1">
                    {pillDetail.statLabel}
                  </div>
                  <div className="text-4xl font-display font-black" style={{ color: pillDetail.color }}>
                    {pillDetail.stat}
                  </div>
                </div>
              )}
              <Link
                to={pillDetail.to}
                onClick={() => setPillDetail(null)}
                data-testid="admin-hub-pill-detail-open"
                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full font-semibold text-sm text-[#05050a] hover:brightness-110 transition"
                style={{ background: pillDetail.color }}
              >
                Open {pillDetail.title} <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const currency = (cents) =>
  `$${((cents || 0) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

// Revenue tile — pulses on new payment (compares latest_paid.paid_at across polls)
const RevenueTile = ({ revenue }) => {
  const navigate = useNavigate();
  const lastPaidRef = useRef(revenue?.latest_paid?.paid_at || null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const nextPaidAt = revenue?.latest_paid?.paid_at || null;
    if (nextPaidAt && nextPaidAt !== lastPaidRef.current) {
      lastPaidRef.current = nextPaidAt;
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 2400);
      return () => clearTimeout(t);
    }
  }, [revenue?.latest_paid?.paid_at]);

  const cards = [
    { label: "Today", data: revenue.today, color: "#00f0ff" },
    { label: "This week", data: revenue.week, color: "#a78bfa" },
    { label: "This month", data: revenue.month, color: "#22c55e" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.5 }}
      onClick={() => navigate("/admin/payments")}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/admin/payments"); } }}
      className={`mt-6 rounded-3xl border bg-white/[0.02] backdrop-blur-md p-6 md:p-7 relative overflow-hidden cursor-pointer hover:border-[#22c55e]/30 focus:outline-none focus:ring-2 focus:ring-[#22c55e]/40 ${
        pulse ? "border-[#22c55e]/60 shadow-[0_0_60px_-10px_rgba(34,197,94,0.5)]" : "border-white/10"
      } transition-all duration-500`}
      data-testid="admin-hub-revenue"
    >
      {pulse && (
        <div
          className="absolute inset-0 pointer-events-none animate-pulse-fade"
          style={{ background: "radial-gradient(circle at 50% 0%, rgba(34,197,94,0.15), transparent 70%)" }}
        />
      )}
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#22c55e]/15 border border-[#22c55e]/40 text-[#22c55e] flex items-center justify-center">
            <DollarSign size={16} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">Revenue</div>
            <div className="font-display font-bold text-lg tracking-tight">Stripe payments · live</div>
          </div>
        </div>
        {pulse && (
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-[#22c55e]" data-testid="revenue-pulse">
            <span className="w-2 h-2 rounded-full bg-[#22c55e] animate-ping" />
            New payment
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {cards.map((c) => (
          <div
            key={c.label}
            data-testid={`revenue-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-3"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">{c.label}</div>
            <div className="font-display font-black text-3xl mt-1" style={{ color: c.color }}>
              {currency(c.data?.total_cents || 0)}
            </div>
            <div className="text-[11px] text-white/40 mt-1">
              {c.data?.count || 0} payment{(c.data?.count || 0) === 1 ? "" : "s"}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};


const SnapStat = ({ label, value, color, to, testid, badge }) => {
  const navigate = useNavigate();
  const clickable = !!to;
  const handle = () => {
    if (clickable) navigate(to);
  };
  const commonProps = {
    "data-testid": testid,
    onClick: handle,
    onKeyDown: (e) => {
      if (!clickable) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handle();
      }
    },
    role: clickable ? "button" : undefined,
    tabIndex: clickable ? 0 : undefined,
    "aria-label": clickable ? `${label} — ${value}. Open details.` : undefined,
    className:
      "relative rounded-2xl bg-white/[0.02] border border-white/10 px-4 py-3 " +
      (clickable
        ? "cursor-pointer hover:bg-white/[0.06] hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all active:scale-[0.98] select-none"
        : ""),
    style: clickable
      ? { boxShadow: `inset 0 0 0 1px transparent`, "--tw-ring-color": color }
      : undefined,
  };
  return (
    <div {...commonProps}>
      {badge > 0 && (
        <span
          className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-[#ff0055] text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#0a0510]"
          data-testid={`${testid}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] truncate">
        {label}
      </div>
      <div className="text-2xl md:text-2xl font-display font-black mt-1 flex items-baseline justify-between gap-2" style={{ color }}>
        <span>{value}</span>
        {clickable && (
          <ArrowUpRight size={14} className="opacity-40 group-hover:opacity-100 shrink-0" style={{ color }} />
        )}
      </div>
    </div>
  );
};

const _relTime = (d) => {
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// Icon lookup for activity rows
const ROW_ICON = {
  booking: Calendar,
  guest: Users,
  chat: MessageSquare,
  campaign: Send,
};

const ActivityRow = ({ event, index, onActed }) => {
  const Icon = ROW_ICON[event.type] || Send;
  const at = event.at ? new Date(event.at) : null;
  const relative = at ? _relTime(at) : "";
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState(null); // "approve" | "decline" | null

  const subLower = (event.sub || "").toLowerCase();
  // Only surface actions for pending items in the correct entity types
  const canApprove =
    (event.type === "booking" && /pending/.test(subLower)) ||
    (event.type === "guest" && /pending/i.test(event.title));
  const canDecline = canApprove;

  const runAction = async (decision) => {
    setActing(true);
    try {
      const headers = authHeaders();
      if (event.type === "booking") {
        const nextStatus = decision === "approve" ? "booked" : "declined";
        await axios.patch(
          `${API}/bookings/${event.id}`,
          { status: nextStatus },
          { headers },
        );
      } else if (event.type === "guest") {
        const nextStatus = decision === "approve" ? "approved" : "declined";
        await axios.patch(
          `${API}/guest-requests/${event.id}`,
          { status: nextStatus },
          { headers },
        );
      }
      toast.success(`${event.type === "booking" ? "Booking" : "Guest"} ${decision === "approve" ? "approved" : "declined"}.`);
      setConfirm(null);
      onActed && onActed();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed.");
    } finally {
      setActing(false);
    }
  };

  return (
    <li className="relative">
      <div
        data-testid={`admin-hub-activity-${event.type}-${index}`}
        className="group flex items-center gap-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] px-4 py-3 hover:bg-white/[0.05] hover:border-white/15 transition"
      >
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 border"
          style={{
            background: `${event.color}18`,
            borderColor: `${event.color}45`,
            color: event.color,
          }}
        >
          <Icon size={14} />
        </div>
        <Link
          to={event.link || "/admin"}
          className="flex-1 min-w-0"
          data-testid={`admin-hub-activity-open-${event.type}-${index}`}
        >
          <div className="text-sm font-semibold text-white truncate">{event.title}</div>
          {event.sub && <div className="text-[11px] text-[#a395c3] mt-0.5 truncate">{event.sub}</div>}
        </Link>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Inline action buttons — only for pending bookings/guests */}
          {canApprove && !confirm && (
            <div className="hidden group-hover:flex items-center gap-1.5">
              <button
                onClick={() => setConfirm("approve")}
                disabled={acting}
                data-testid={`admin-hub-approve-${event.type}-${index}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/50 text-[#22c55e] text-[11px] font-semibold hover:bg-[#22c55e] hover:text-[#05050a] transition"
              >
                <Check size={11} /> Approve
              </button>
              <button
                onClick={() => setConfirm("decline")}
                disabled={acting}
                data-testid={`admin-hub-decline-${event.type}-${index}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#ff0055]/12 border border-[#ff0055]/50 text-[#ff5577] text-[11px] font-semibold hover:bg-[#ff0055] hover:text-white transition"
              >
                <X size={11} /> Decline
              </button>
            </div>
          )}
          {canApprove && confirm && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/70">
                Confirm {confirm}?
              </span>
              <button
                onClick={() => runAction(confirm)}
                disabled={acting}
                data-testid={`admin-hub-confirm-${event.type}-${index}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition disabled:opacity-60"
                style={{
                  background: confirm === "approve" ? "#22c55e" : "#ff0055",
                  color: confirm === "approve" ? "#05050a" : "#ffffff",
                }}
              >
                {acting ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                Yes
              </button>
              <button
                onClick={() => setConfirm(null)}
                disabled={acting}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 text-white/70 flex items-center justify-center"
                data-testid={`admin-hub-cancel-${event.type}-${index}`}
              >
                <X size={11} />
              </button>
            </div>
          )}
          {!confirm && (
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">
              {relative}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

export default AdminHub;

/**
 * MobileCategoryNav — mobile-only (md:hidden) accordion that groups the top-
 * level admin tiles by category so the phone landing page shows 4 tap-to-
 * expand rows instead of a 10-card vertical wall.
 *
 * Behaviour:
 *  - First render: nothing expanded (clean overview).
 *  - Rolls up any `stat` on child cards into a category counter so admins
 *    know where the action is at a glance.
 *  - Each child card is a Link so tapping it navigates directly.
 */
const MobileCategoryNav = ({ categories, cards }) => {
  const [openKey, setOpenKey] = useState(null);
  const byCategory = categories.map((cat) => ({
    ...cat,
    items: cards.filter((c) => c.category === cat.key),
  })).filter((cat) => cat.items.length > 0);

  const rollup = (items) => {
    // Sum numeric stats across children — best-effort "how much is pending".
    let total = 0;
    let anyStat = false;
    items.forEach((c) => {
      if (typeof c.stat === "number") {
        total += c.stat;
        anyStat = true;
      }
    });
    return anyStat ? total : null;
  };

  return (
    <div className="md:hidden space-y-3" data-testid="admin-hub-mobile-nav">
      {byCategory.map((cat) => {
        const CatIcon = cat.icon;
        const isOpen = openKey === cat.key;
        const rolled = rollup(cat.items);
        return (
          <div
            key={cat.key}
            data-testid={`admin-hub-mobile-cat-${cat.key}`}
            className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md overflow-hidden"
          >
            <button
              type="button"
              onClick={() => setOpenKey(isOpen ? null : cat.key)}
              data-testid={`admin-hub-mobile-cat-toggle-${cat.key}`}
              className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.02] transition-colors"
              aria-expanded={isOpen}
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center border flex-shrink-0"
                style={{
                  background: `${cat.color}18`,
                  borderColor: `${cat.color}45`,
                  color: cat.color,
                }}
              >
                <CatIcon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-display font-bold text-base tracking-tight">
                    {cat.label}
                  </span>
                  {rolled !== null && rolled > 0 && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                      style={{ background: `${cat.color}22`, color: cat.color }}
                    >
                      {rolled}
                    </span>
                  )}
                  <span className="text-[10px] text-white/40 ml-auto">
                    {cat.items.length}
                  </span>
                </div>
                <div className="text-[11px] text-[#a395c3] mt-0.5 truncate">
                  {cat.sub}
                </div>
              </div>
              <ChevronDown
                size={16}
                className={`text-white/50 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="body"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-3 pt-1 space-y-2 border-t border-white/5">
                    {cat.items.map((c) => {
                      const Icon = c.icon;
                      return (
                        <Link
                          key={c.key}
                          to={c.to}
                          data-testid={`admin-hub-mobile-item-${c.key}`}
                          className="flex items-center gap-3 rounded-xl p-3 bg-white/[0.02] border border-white/10 hover:bg-white/[0.05] transition-colors"
                        >
                          <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center border flex-shrink-0"
                            style={{
                              background: `${c.color}18`,
                              borderColor: `${c.color}45`,
                              color: c.color,
                            }}
                          >
                            <Icon size={15} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-semibold text-[13px] tracking-tight truncate">
                              {c.title}
                            </div>
                            <div className="text-[10px] text-[#a395c3] mt-0.5 truncate">
                              {c.sub}
                            </div>
                          </div>
                          {typeof c.stat === "number" && (
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                              style={{ background: `${c.color}22`, color: c.color }}
                            >
                              {c.stat}
                            </span>
                          )}
                          <ArrowUpRight
                            size={14}
                            className="text-white/40 flex-shrink-0"
                          />
                        </Link>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
